import { formatCivilDate, parseCivilDate } from '@klopotun/domain';
import { Calendar } from 'lucide-react';
import { useId, useState } from 'react';
import type { Ref } from 'react';
import { DayPicker } from 'react-day-picker';
import type { ClassNames } from 'react-day-picker';
import { uk } from 'react-day-picker/locale/uk';
import { BottomSheet } from '../bottom-sheet/bottom-sheet.js';
import styles from './date-field.module.css';

type DateFieldProps = {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  ref?: Ref<HTMLButtonElement>;
  placeholder?: string;
  hint?: string;
  error?: string;
  clearable?: boolean;
  disabled?: boolean;
};

const ICON_SIZE = 20;
const YEARS_AROUND_TODAY = 10;
const LAST_MONTH_INDEX = 11;
const CIVIL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const CALENDAR_CLASS_NAMES: Partial<ClassNames> = {
  root: styles.calendar,
  months: styles.months,
  month: styles.month,
  month_caption: styles.caption,
  caption_label: styles.captionLabel,
  dropdowns: styles.dropdowns,
  dropdown_root: styles.dropdownRoot,
  dropdown: styles.dropdown,
  button_previous: styles.navButton,
  button_next: styles.navButton,
  chevron: styles.chevron,
  month_grid: styles.grid,
  weekday: styles.weekday,
  day: styles.day,
  day_button: styles.dayButton,
  selected: styles.selected,
  today: styles.today,
  outside: styles.outside,
  disabled: styles.disabled,
  hidden: styles.hidden,
};

const padded = (value: number, length: number): string => String(value).padStart(length, '0');

const toCalendarDate = (value: string): Date | undefined => {
  if (!CIVIL_DATE_PATTERN.test(value)) {
    return undefined;
  }

  const { year, month, day } = parseCivilDate(value);
  return new Date(year, month - 1, day);
};

const toFieldValue = (date: Date): string =>
  formatCivilDate({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });

const formatForPeople = (date: Date): string =>
  `${padded(date.getDate(), 2)}.${padded(date.getMonth() + 1, 2)}.${padded(date.getFullYear(), 4)}`;

export const DateField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  ref,
  placeholder = 'Оберіть дату',
  hint,
  error,
  clearable = false,
  disabled = false,
}: DateFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const valueId = `${fieldId}-value`;
  const messageId = `${fieldId}-message`;
  const hasError = error !== undefined;
  const message = error ?? hint;
  const selectedDate = toCalendarDate(value);
  const hasValue = selectedDate !== undefined;
  const canClear = clearable && hasValue;
  const today = new Date();

  const closeCalendar = (): void => {
    setIsOpen(false);
    onBlur?.();
  };

  const selectDate = (date: Date): void => {
    onChange(toFieldValue(date));
    closeCalendar();
  };

  const clearDate = (): void => {
    onChange('');
    closeCalendar();
  };

  return (
    <div className={styles.field}>
      <span id={labelId} className={styles.label}>
        {label}
      </span>
      <button
        ref={ref}
        type="button"
        name={name}
        className={`${styles.trigger} ${hasError ? styles.invalid : ''}`}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-labelledby={`${labelId} ${valueId}`}
        aria-invalid={hasError}
        aria-describedby={message === undefined ? undefined : messageId}
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <span id={valueId} className={hasValue ? styles.value : styles.placeholder}>
          {hasValue ? formatForPeople(selectedDate) : placeholder}
        </span>
        <Calendar className={styles.icon} size={ICON_SIZE} />
      </button>
      {message !== undefined && (
        <p
          id={messageId}
          className={hasError ? styles.error : styles.hint}
          role={hasError ? 'alert' : undefined}
        >
          {message}
        </p>
      )}
      <BottomSheet open={isOpen} onClose={closeCalendar} title={label}>
        <DayPicker
          mode="single"
          required
          selected={selectedDate}
          onSelect={selectDate}
          defaultMonth={selectedDate ?? today}
          startMonth={new Date(today.getFullYear() - YEARS_AROUND_TODAY, 0)}
          endMonth={new Date(today.getFullYear() + YEARS_AROUND_TODAY, LAST_MONTH_INDEX)}
          captionLayout="dropdown"
          navLayout="around"
          showOutsideDays
          fixedWeeks
          locale={uk}
          classNames={CALENDAR_CLASS_NAMES}
        />
        {canClear && (
          <button type="button" className={styles.clear} onClick={clearDate}>
            Очистити дату
          </button>
        )}
      </BottomSheet>
    </div>
  );
};
